import { HAZE_CHUNK } from './terrain.glsl';

/**
 * Ocean shaders (SPEC §3.9.1): three Gerstner wave trains, a depth-ramped colour, shoreline foam
 * from terrain proximity, and a reflection approximation.
 *
 * "The sea at grazing angle is the most-visible surface in the game — it deserves the shader
 * budget", and most of that budget goes on the two things that read at a glance: the *shape* of
 * the swell, which Gerstner gives and a sine sum does not, and the depth ramp that turns the
 * shoreline into a readable feature rather than a hard line.
 *
 * Gerstner waves displace horizontally as well as vertically, which is what produces the sharp
 * crests and broad troughs of real water. The normal is derived analytically from the same
 * partial derivatives, so shading and silhouette can never disagree.
 */

export const OCEAN_VERTEX = /* glsl */ `
precision highp float;

#define WAVE_COUNT 3

uniform vec4 uWaves[WAVE_COUNT]; // directionX, directionZ, amplitude, frequency
uniform vec3 uWaveExtra[WAVE_COUNT]; // steepness, phaseSpeed, unused
uniform float uTime;
uniform vec3 uCameraPosition;
uniform float uDisplacementFadeM;

varying vec3 vWorldPosition;
varying vec3 vNormal;
varying float vViewDistance;
varying float vCrest;

void main() {
  // The grid is camera-centred; wave phase uses the world position so the swell stays put in
  // the world rather than sliding along with the aircraft.
  vec3 base = vec3(position.x + uCameraPosition.x, 0.0, position.z + uCameraPosition.z);

  float distanceM = length(base.xz - uCameraPosition.xz);
  // Beyond this range the grid cannot resolve a wavelength, and displacing it only aliases.
  float fade = 1.0 - smoothstep(uDisplacementFadeM * 0.55, uDisplacementFadeM, distanceM);

  vec3 displaced = base;
  vec3 tangent = vec3(1.0, 0.0, 0.0);
  vec3 binormal = vec3(0.0, 0.0, 1.0);
  float crest = 0.0;

  for (int i = 0; i < WAVE_COUNT; i++) {
    vec2 direction = uWaves[i].xy;
    float amplitude = uWaves[i].z * fade;
    float frequency = uWaves[i].w;
    float steepness = uWaveExtra[i].x;
    float phase = uWaveExtra[i].y;

    float theta = frequency * dot(direction, base.xz) + uTime * phase;
    float cosTheta = cos(theta);
    float sinTheta = sin(theta);
    float qa = steepness * amplitude;

    displaced.x += qa * direction.x * cosTheta;
    displaced.z += qa * direction.y * cosTheta;
    displaced.y += amplitude * sinTheta;

    float wa = frequency * amplitude;
    tangent.x -= steepness * direction.x * direction.x * wa * sinTheta;
    tangent.z -= steepness * direction.x * direction.y * wa * sinTheta;
    tangent.y += direction.x * wa * cosTheta;

    binormal.x -= steepness * direction.x * direction.y * wa * sinTheta;
    binormal.z -= steepness * direction.y * direction.y * wa * sinTheta;
    binormal.y += direction.y * wa * cosTheta;

    crest += sinTheta * amplitude;
  }

  vWorldPosition = displaced;
  vNormal = normalize(cross(binormal, tangent));
  vViewDistance = distance(uCameraPosition, displaced);
  vCrest = crest;

  gl_Position = projectionMatrix * viewMatrix * vec4(displaced, 1.0);
}
`;

export const OCEAN_FRAGMENT = /* glsl */ `
precision highp float;

uniform sampler2D uHeightMap;
uniform sampler2D uNormalMap;
uniform vec2 uWorldOrigin;
uniform float uWorldSize;
uniform float uHeightScale;
uniform float uHeightOffset;
uniform float uTime;

uniform vec3 uSunDirection;
uniform vec3 uSunColour;
uniform vec3 uZenithColour;
uniform vec3 uHorizonColour;
uniform vec3 uCameraPosition;

uniform vec3 uShallowColour;
uniform vec3 uDeepColour;
uniform float uDepthRampM;
uniform float uFoamWidthM;
uniform float uFoamStrength;
uniform float uFresnelF0;
uniform float uSpecularPower;

varying vec3 vWorldPosition;
varying vec3 vNormal;
varying float vViewDistance;
varying float vCrest;

${HAZE_CHUNK}

float terrainHeightAt(vec2 worldXZ) {
  vec2 uv = (worldXZ - uWorldOrigin) / uWorldSize;
  return texture2D(uHeightMap, clamp(uv, 0.0, 1.0)).r * uHeightScale + uHeightOffset;
}

/** The same analytic sky the dome draws, so reflections and background agree. */
vec3 skyColour(vec3 direction) {
  float t = pow(clamp(direction.y, 0.0, 1.0), 0.42);
  vec3 sky = mix(uHorizonColour, uZenithColour, t);
  float sun = pow(max(dot(direction, uSunDirection), 0.0), 320.0);
  return sky + uSunColour * sun * 3.0;
}

void main() {
  float seabed = terrainHeightAt(vWorldPosition.xz);

  // Land wins: the terrain mesh draws the shore, and water above it would z-fight the beach.
  if (seabed > 0.0) discard;

  float depth = -seabed;

  // Two scrolling normal layers for the ripple the Gerstner grid is too coarse to carry.
  vec2 rippleUv = vWorldPosition.xz * 0.055;
  vec3 rippleA = texture2D(uNormalMap, rippleUv + vec2(uTime * 0.009, uTime * 0.005)).rgb * 2.0 - 1.0;
  vec3 rippleB = texture2D(uNormalMap, rippleUv * 2.7 - vec2(uTime * 0.014, uTime * 0.011)).rgb * 2.0 - 1.0;
  float rippleFade = 1.0 - smoothstep(400.0, 2200.0, vViewDistance);
  vec3 ripple = normalize(vec3((rippleA.x + rippleB.x * 0.6), 2.4, (rippleA.y + rippleB.y * 0.6)));
  vec3 normal = normalize(mix(normalize(vNormal), normalize(normalize(vNormal) + ripple * 0.55), rippleFade));

  vec3 viewDirection = normalize(uCameraPosition - vWorldPosition);
  vec3 reflected = reflect(-viewDirection, normal);
  reflected.y = abs(reflected.y);

  // Depth ramp: shallow water over the shelf, near-black over the drop-off.
  vec3 body = mix(uShallowColour, uDeepColour, smoothstep(0.0, uDepthRampM, depth));

  // Schlick fresnel. At the grazing angles this surface is usually seen at, the reflection is
  // most of what the player sees, which is why the sky and the sea must share a colour model.
  float cosView = clamp(dot(normal, viewDirection), 0.0, 1.0);
  float fresnel = uFresnelF0 + (1.0 - uFresnelF0) * pow(1.0 - cosView, 5.0);
  vec3 reflection = skyColour(reflected);

  vec3 colour = mix(body, reflection, clamp(fresnel, 0.0, 1.0));

  // Sun glitter.
  vec3 halfVector = normalize(viewDirection + uSunDirection);
  float specular = pow(max(dot(normal, halfVector), 0.0), uSpecularPower);
  colour += uSunColour * specular * 1.6;

  // Foam where the water shoals, strongest on the crests — the cue that turns a colour ramp
  // into a shoreline.
  float shoal = 1.0 - smoothstep(0.0, uFoamWidthM, depth);
  float crest = smoothstep(0.15, 0.75, vCrest + 0.35);
  float foam = clamp(shoal * (0.45 + 0.55 * crest), 0.0, 1.0) * uFoamStrength;
  colour = mix(colour, vec3(0.86, 0.92, 0.93), foam);

  gl_FragColor = vec4(applyHaze(colour, vViewDistance), 1.0);
}
`;
